import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HlsMobileMenu from "@/components/site/HlsMobileMenu";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/pages/HeroLightStudy.css";
import "@/components/site/HlsMobileMenu.css";
import "@/components/site/SiteHeader.css";

/* ------------------------------------------------------------------
   SiteHeader — global page chrome for non-homepage routes.

   • Visual: re-uses the existing brand-mark + nav class system
     (`hls__*`) from the homepage so the design is identical, but
     locked into the post-intro settled state.

   • Behaviour (this file): hide-on-scroll-down / show-on-scroll-up.
     Always visible at the very top of the page; never hidden while
     the mobile overlay menu is open; reset to visible on every
     route change.

   Process and Studio are in-page homepage anchors — from non-home
   routes we navigate to `/` and then scroll to the section.
   ------------------------------------------------------------------ */
export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  // ----- navigation helpers -----
  const goHome = (e) => {
    e?.preventDefault?.();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const goToRoute = (to) => (e) => {
    e.preventDefault();
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(to);
    }
  };

  const scrollToHomeAnchor = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToHomeAnchor = (anchorId) => (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      scrollToHomeAnchor(anchorId);
      return;
    }
    navigate("/");
    [120, 320, 700, 1300].forEach((ms) =>
      setTimeout(() => scrollToHomeAnchor(anchorId), ms)
    );
  };

  // On arrival at `/` with a hash, scroll the matching section into
  // view across a few frames so pinned sections have time to settle.
  useEffect(() => {
    if (location.pathname !== "/" || !location.hash) return;
    const id = location.hash.replace(/^#/, "");
    [80, 250, 600, 1100].forEach((ms) =>
      setTimeout(() => scrollToHomeAnchor(id), ms)
    );
  }, [location.pathname, location.hash]);

  const onMobileScrollTo = (id) => {
    if (location.pathname === "/") {
      scrollToHomeAnchor(id);
    } else {
      navigate("/");
      [120, 320, 700, 1300].forEach((ms) =>
        setTimeout(() => scrollToHomeAnchor(id), ms)
      );
    }
  };
  const onMobileNavigate = (to) => navigate(to);

  // ----- hide-on-scroll behaviour -----
  //
  // TOP_ZONE — below this scroll-Y the header is always shown.
  // DELTA    — minimum directional delta before we flip state, so
  //            tiny accidental jitters / momentum noise are ignored.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const TOP_ZONE = 100;
    const DELTA = 12;

    let lastY = window.scrollY;
    let dirAnchorY = lastY;
    let lastDir = 0; // -1 up, +1 down, 0 unknown
    let raf = null;

    const update = () => {
      raf = null;
      const y = window.scrollY;
      const diff = y - lastY;

      // Always show inside the top zone — and reset all anchors so
      // the next downward gesture has a clean delta baseline.
      if (y <= TOP_ZONE) {
        setHidden((h) => (h ? false : h));
        lastY = y;
        dirAnchorY = y;
        lastDir = 0;
        return;
      }

      const dir = diff > 0 ? 1 : diff < 0 ? -1 : lastDir;
      if (dir !== 0 && dir !== lastDir) {
        // Direction changed — anchor at the turn point (previous Y),
        // not the current Y, so the very next frame already exposes
        // the full delta of the new direction.
        dirAnchorY = lastY;
        lastDir = dir;
      }

      const dirDelta = Math.abs(y - dirAnchorY);
      if (dirDelta >= DELTA) {
        if (lastDir === 1) setHidden((h) => (h ? h : true));
        else if (lastDir === -1) setHidden((h) => (h ? false : h));
      }

      lastY = y;
    };

    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Force header visible whenever the mobile menu opens, and reset on
  // route change so the user always sees the chrome on a new page.
  useEffect(() => {
    if (open) setHidden(false);
  }, [open]);

  useEffect(() => {
    setHidden(false);
  }, [location.pathname]);

  const isHidden = hidden && !open;

  return (
    <>
      <div
        className={[
          "hls__brand-mark",
          "is-entered",
          "descriptor-out",
          "is-top",
          "is-static-header",
          isHidden ? "is-scrolled-away" : "",
        ].join(" ")}
        data-testid="site-header-brand"
        data-hidden={isHidden ? "true" : "false"}
      >
        <div className="hls__brand-inner">
          <h1 className="hls__brand-name">
            <button
              type="button"
              className="hls__brand-name-btn brand-wordmark"
              onClick={goHome}
              aria-label="Ōraṇi — Home"
              data-testid="site-header-brand-home"
            >
              Ōraṇi
            </button>
          </h1>
        </div>
      </div>

      <nav
        className={[
          "hls__nav",
          "is-visible",
          "is-static-header",
          open ? "has-menu-open" : "",
          isHidden ? "is-scrolled-away" : "",
        ].join(" ")}
        aria-label="Primary"
        data-testid="site-header-nav"
        data-hidden={isHidden ? "true" : "false"}
        data-reduced-motion={reducedMotion ? "true" : "false"}
      >
        <ul className="hls__nav-list hls__nav-list--left">
          <li>
            <a
              href="/work"
              onClick={goToRoute("/work")}
              data-testid="site-nav-work"
            >
              Work
            </a>
          </li>
          <li>
            <a
              href="/#process-section"
              onClick={goToHomeAnchor("process-section")}
              data-testid="site-nav-process"
            >
              Process
            </a>
          </li>
          <li>
            <a
              href="/press"
              onClick={goToRoute("/press")}
              data-testid="site-nav-press"
            >
              Press
            </a>
          </li>
        </ul>

        <div className="hls__nav-brand-slot" aria-hidden="true" />

        <ul className="hls__nav-list hls__nav-list--right">
          <li>
            <a
              href="/#studio"
              onClick={goToHomeAnchor("studio")}
              data-testid="site-nav-studio"
            >
              Studio
            </a>
          </li>
          <li>
            <a
              href="/enquire"
              onClick={goToRoute("/enquire")}
              data-testid="site-nav-enquire"
            >
              Enquire
            </a>
          </li>
          <li>
            <a
              href="/careers"
              onClick={goToRoute("/careers")}
              data-testid="site-nav-careers"
            >
              Careers
            </a>
          </li>
        </ul>

        <button
          type="button"
          className="hls__nav-menu-btn"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="hls-mobile-menu"
          onClick={() => setOpen((s) => !s)}
          data-testid="site-nav-menu"
        >
          <span className="hls__nav-menu-icon" aria-hidden="true">
            <span className="hls__nav-menu-icon-bar" />
            <span className="hls__nav-menu-icon-bar" />
          </span>
        </button>
      </nav>

      <HlsMobileMenu
        open={open}
        onClose={() => setOpen(false)}
        onScrollTo={onMobileScrollTo}
        onNavigate={onMobileNavigate}
      />
    </>
  );
}
